/**
 * The default configuration of the passport page.
 *
 * Each entry will be added to the app in the order of definition.
 * Might be extended by custom brand designs side loaded via firebase remote config.
 */

/**
 * Definitions of entries
 *
 * Headers:
 * {
 *   labelContent: 'Your Bike', // optional, exact content, overrides labelTranslationKey if present
 *   labelTranslationKey: 'YOUR_BIKE', // the key in the translations to use if no content present (optional if labelContent is used)
 *   type: 'header', // type for headers
 * }
 *
 * Inputs:
 * {
 *   // mandatory
 *   domId: 'manufacturer', // the DOM id to use, important for tests
 *   labelTranslationKey: 'MANUFACTURER', // the key in the translations to use if no content present (optional if labelContent is used)
 *   passportKey: 'manufacturer', // the field in the passport attributes
 *
 *   // optionals
 *   conditionalAllowedValues: [true], // // optional, allowed values for the key that will result in showing the input if matched
 *   conditionalForbiddenValues: ['car', 'motorcycle'], // optional, forbidden values for the key that will result in hiding the input if matched
 *   conditionalPassportKey: 'bikeType', // optional, passport key to check in order to show/hide this input
 *   disabled: true, // optional, disables the input so it only displays the data
 *   hidden: true, // optional, hides the entry from the user
 *   hintContent: 'Enter value', // optional, exact content, overrides hintTranslationKey if present
 *   hintTranslationKey: 'SPECIAL_FEATURES_PLACEHOLDER', // optional, translation key for placeholder texts
 *   labelContent: 'Manufacturer', // optional, exact content, overrides labelTranslationKey if present
 *
 *   // text and number
 *   textLength: 16, // max length for "text" types
 *   type: 'text', // the type of the input
 *
 *   // textarea
 *   type: 'textarea', // the type of the input
 *
 *   // date
 *   dateMin: '2010-01-01', // min date for the select
 *   displayFormat: 'DD.MM.YYYY', // the display format of the date in the select
 *   type: 'datetime', // the type of the input
 *
 *   // select
 *   selectOptionsAllowOther: true, // optional, wether to allow "other" inputs besides the options
 *   selectOptions: [{ // the options of this select
 *     labelContent: 'Black', // optional, exact content, overrides labelTranslationKey if present
 *     labelTranslationKey: 'COLOUR_BLACK', // the key in the translations to use if no content present (optional if labelContent is used)
 *     value: 'black', // the value stored in passport
 *   }],
 *   type: 'select', // the type of the input
 *
 *   // toggle
 *   type: 'toggle', // the type of the input
 * }
 */

/**
 * Overriding via firebase remote config
 *
 * - passport keys are used to define which fields will be altered.
 * - all keys inside will override the existing defaults with given values
 *
 * Example:
 *
 * {
 *   // this disables the default free text field and enters a predefined value
 *   "manufacturer": {
 *     "defaultValue": "Giant",
 *     "disabled": true
 *   },
 *
 *   // this replaces the default free text field with a select containing only two options
 *   "model": {
 *     "type": "select",
 *     "selectOptions": [{
 *       "labelContent": "Trance 2",
 *       "value": "trance2"
 *     }, {
 *       "labelContent": "Trance X",
 *       "value": "trancex"
 *     }]
 *   },
 *
 *   // this reduces the choices of the existing select
 *   "colour": {
 *     "selectOptions": [{
 *       "labelTranslationKey": "COLOUR_BLACK",
 *       "value": "black"
 *     }, {
 *       "labelTranslationKey": "COLOUR_BLUE",
 *       "value": "blue"
 *     }]
 *   }
 */

export const passportConfiguration = [
  {
    // bike
    labelTranslationKey: 'YOUR_BIKE',
    type: 'header',
  },
  {
    domId: 'manufacturer',
    labelTranslationKey: 'MANUFACTURER',
    passportKey: 'manufacturer',
    textLength: 32,
    type: 'text',
  },
  {
    domId: 'model',
    labelTranslationKey: 'MODEL',
    passportKey: 'model',
    textLength: 32,
    type: 'text',
  },
  {
    dateMin: '2000',
    displayFormat: 'YYYY',
    domId: 'model_year',
    labelTranslationKey: 'MODEL_YEAR',
    passportKey: 'modelYear',
    type: 'datetime',
  },
  {
    domId: 'colour',
    labelTranslationKey: 'COLOUR',
    passportKey: 'colour',
    selectOptionsAllowOther: true,
    selectOptions: [
      {
        labelTranslationKey: 'COLOUR_BLACK',
        value: 'black',
      },
      {
        labelTranslationKey: 'COLOUR_BLUE',
        value: 'blue',
      },
      {
        labelTranslationKey: 'COLOUR_BROWN',
        value: 'brown',
      },
      {
        labelTranslationKey: 'COLOUR_GREEN',
        value: 'green',
      },
      {
        labelTranslationKey: 'COLOUR_GREY',
        value: 'grey',
      },
      {
        labelTranslationKey: 'COLOUR_ORANGE',
        value: 'orange',
      },
      {
        labelTranslationKey: 'COLOUR_PINK',
        value: 'pink',
      },
      {
        labelTranslationKey: 'COLOUR_PURPLE',
        value: 'purple',
      },
      {
        labelTranslationKey: 'COLOUR_RED',
        value: 'red',
      },
      {
        labelTranslationKey: 'COLOUR_SILVER',
        value: 'silver',
      },
      {
        labelTranslationKey: 'COLOUR_WHITE',
        value: 'white',
      },
      {
        labelTranslationKey: 'COLOUR_YELLOW',
        value: 'yellow',
      },
    ],
    type: 'select',
  },
  {
    domId: 'bike_type',
    labelTranslationKey: 'BIKE_TYPE',
    passportKey: 'bikeType',
    selectOptionsAllowOther: true,
    selectOptions: [
      {
        labelTranslationKey: 'BIKE_TYPE_CITY',
        value: 'city',
      },
      {
        labelTranslationKey: 'BIKE_TYPE_DOWNHILL',
        value: 'downhill',
      },
      {
        labelTranslationKey: 'BIKE_TYPE_FOLDING',
        value: 'folding',
      },
      {
        labelTranslationKey: 'BIKE_TYPE_FREIGHT',
        value: 'freight',
      },
      {
        labelTranslationKey: 'BIKE_TYPE_KIDS',
        value: 'kids',
      },
      {
        labelTranslationKey: 'BIKE_TYPE_MTB',
        value: 'mtb',
      },
      {
        labelTranslationKey: 'BIKE_TYPE_RECUMBENT',
        value: 'recumbent',
      },
      {
        labelTranslationKey: 'BIKE_TYPE_ROAD',
        value: 'road',
      },
      {
        labelTranslationKey: 'BIKE_TYPE_TANDEM',
        value: 'tandem',
      },
      {
        labelTranslationKey: 'BIKE_TYPE_TREKKING',
        value: 'trekking',
      },
      {
        labelTranslationKey: 'MOTORCYCLE',
        value: 'motorcycle',
      },
      {
        labelTranslationKey: 'CAR',
        value: 'car',
      },
      {
        labelTranslationKey: 'BOAT',
        value: 'boat',
      },
    ],
    type: 'select',
  },
  {
    domId: 'engine',
    labelTranslationKey: 'ENGINE',
    passportKey: 'engine',
    selectOptionsAllowOther: true,
    selectOptions: [
      {
        labelContent: 'Bafang',
        value: 'bafang',
      },
      {
        labelContent: 'Bosch',
        value: 'bosch',
      },
      {
        labelContent: 'Brose',
        value: 'brose',
      },
      {
        labelContent: 'Shimano',
        value: 'shimano',
      },
      {
        labelContent: 'TQ',
        value: 'tq',
      },
      {
        labelContent: 'Yamaha',
        value: 'yamaha',
      },
    ],
    type: 'select',
  },
  {
    // miscellaneous
    labelTranslationKey: 'MISCELLANEOUS',
    type: 'header',
  },
  {
    conditionalForbiddenValues: ['car', 'motorcycle', 'boat'],
    conditionalPassportKey: 'bikeType',
    domId: 'shifting_system_manufacturer',
    labelTranslationKey: 'SHIFTING_SYSTEM_MANUFACTURER',
    passportKey: 'shiftingSystemManufacturer',
    textLength: 32,
    type: 'text',
  },
  {
    conditionalForbiddenValues: ['car', 'motorcycle', 'boat'],
    conditionalPassportKey: 'bikeType',
    domId: 'shifting_system_model',
    labelTranslationKey: 'SHIFTING_SYSTEM_MDOEL',
    passportKey: 'shiftingSystemModel',
    textLength: 32,
    type: 'text',
  },
  {
    conditionalForbiddenValues: ['car', 'motorcycle', 'boat'],
    conditionalPassportKey: 'bikeType',
    domId: 'shifting_system_gears',
    labelTranslationKey: 'SHIFTING_SYSTEM_GEARS',
    passportKey: 'shiftingSystemGears',
    type: 'number',
  },
  {
    domId: 'special_features',
    hintTranslationKey: 'SPECIAL_FEATURES_PLACEHOLDER',
    labelTranslationKey: 'SPECIAL_FEATURES',
    passportKey: 'specialFeatures',
    textLength: 256,
    type: 'textarea',
  },
  {
    // purchase_details
    labelTranslationKey: 'PURCHASE_DETAILS',
    type: 'header',
  },
  {
    dateMin: '2010-01-01',
    displayFormat: 'DD.MM.YYYY',
    domId: 'purchase_date',
    labelTranslationKey: 'PURCHASE_DATE',
    passportKey: 'purchaseDate',
    type: 'datetime',
  },
  {
    domId: 'price',
    labelTranslationKey: 'PRICE',
    passportKey: 'price',
    type: 'number',
  },
  {
    domId: 'used',
    labelTranslationKey: 'USED',
    passportKey: 'used',
    type: 'toggle',
  },
  {
    domId: 'frame_number',
    labelTranslationKey: 'FRAME_NUMBER',
    passportKey: 'frameNumber',
    textLength: 24,
    type: 'text',
  },
  {
    domId: 'insurance',
    labelTranslationKey: 'INSURANCE',
    passportKey: 'insurance',
    type: 'toggle',
  },
  {
    conditionalAllowedValues: [true],
    conditionalPassportKey: 'insurance',
    domId: 'insurance_company',
    labelTranslationKey: 'INSURANCE_COMPANY',
    passportKey: 'insuranceCompany',
    textLength: 24,
    type: 'text',
  },
  {
    conditionalAllowedValues: [true],
    conditionalPassportKey: 'insurance',
    domId: 'insurance_number',
    labelTranslationKey: 'INSURANCE_NUMBER',
    passportKey: 'insuranceNumber',
    textLength: 24,
    type: 'text',
  },
  {
    domId: 'registration_code',
    hintTranslationKey: 'REGISTRATION_CODE_HINT',
    labelTranslationKey: 'REGISTRATION_CODE',
    passportKey: 'registrationCode',
    textLength: 24,
    type: 'text',
  },
];
